/**
 * ReportsTab - Report catalog with search, category filtering, favorites, and opening.
 * Refactored: styles → styles/reportsTabStyles.ts, helpers → utils/reportsTabHelpers.ts.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
	ActivityIndicator,
	Linking,
	Modal,
	Platform,
	Text,
	TextInput,
	TouchableOpacity,
	View,
	useWindowDimensions,
} from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '../../../../theme/useTheme';
import { useToast } from '../../../../components/LhToastContainer';
import { AppDispatch, RootState } from '../../../../store';
import { setReportConfigurationRows } from '../../../../store/slices/reportingConfigurationSlice';
import reportingConfigurationApi from '../../../Admin/pages/ReportingConfiguration/reportingConfigurationApi';
import reportsFavoritesApi from '../../services/reportsFavoritesApi';
import { ReportCatalogItem, ReportCategory, ReportPlatform } from '../../utils/reportCatalog';
import { buildCategoryCounts, filterReports } from '../../utils/reportsTabUtils';
import {
	CATEGORY_COLORS,
	CATEGORY_COLORS_DARK,
	CATEGORY_FILTER_ORDER,
	FALLBACK_CATEGORY_COLORS,
	FALLBACK_PLATFORM_STYLE,
	FALLBACK_CATEGORY_COLORS_DARK,
	PLATFORM_STYLES,
	PLATFORM_STYLES_DARK,
	REPORT_FAVORITES_STORAGE_KEY,
	getValidIconName,
	inferPlatformFromLink,
	storageAdapter,
} from '../../utils/reportsTabHelpers';
import { createReportsTabStyles } from '../../styles/reportsTabStyles';

interface ReportsTabProps {
	onOpenReport?: (reportId: string) => void;
	/** When true, the parent (AnalyticsHubDashBoard) is already loading the report config – skip local fetch. */
	isReportConfigLoading?: boolean;
	/**
	 * Optional pre-applied search filter pushed from the KPI Overview tab when the user
	 * clicks a KPI card's Power BI / Tableau badge. The value (typically a KPI name)
	 * is applied as the search term so reports tagged with that KPI are surfaced.
	 * The parent is expected to pass `null`/`undefined` once the user navigates away.
	 */
	preAppliedFilter?: string | null;
}

const ReportsTab: React.FC<ReportsTabProps> = ({ onOpenReport, isReportConfigLoading, preAppliedFilter }) => {
	const { colors, resolvedTheme } = useTheme();
	const isDark = resolvedTheme === 'dark';
	const styles = createReportsTabStyles(isDark, colors);
	const dispatch = useDispatch<AppDispatch>();
	const reportConfigRows = useSelector((state: RootState) => state.reportingConfiguration.rows);
	const userGpid = useSelector((state: RootState) => state.appData?.userInfo?.GPID ?? null);
	const userRoleName = useSelector((state: RootState) => state.appData?.userInfo?.roleName ?? '');
	const userRoleId = useSelector((state: RootState) => state.appData?.userInfo?.roleId ?? null);
	const [searchTerm, setSearchTerm] = useState('');
	const [selectedCategories, setSelectedCategories] = useState<ReportCategory[]>([]);
	const [favoritesOnly, setFavoritesOnly] = useState(false);
	const [favoriteIds, setFavoriteIds] = useState<string[]>([]);
	const [favoriteIdMap, setFavoriteIdMap] = useState<Record<string, number>>({});
	const [hoveredReportId, setHoveredReportId] = useState<string | null>(null);
	const [isSearchFocused, setIsSearchFocused] = useState(false);
	const [openingReportId, setOpeningReportId] = useState<string | null>(null);
	const [isLoadingReports, setIsLoadingReports] = useState(true);
	const { width, height } = useWindowDimensions();
	const toast = useToast();
	const hasLoadedFavoritesRef = useRef(false);
	const isWeb = Platform.OS === 'web';
	const configuredCatalog = useMemo<ReportCatalogItem[]>(() => {
		const isSuperUserRoleIdOne = Number(userRoleId) === 1;
		return reportConfigRows
			// Only include 'Active' rows in the visible catalog.
			.filter((row) => {
				const status = row.status ?? (row as any).status;
				const link = (row.link && row.link.trim()).toLowerCase();
				const isRestrictedNavigatorReport =
					link === '/navigator-reports/crewing-analytics'
					|| link === '/navigator-reports/asn-gap';
				return String(status).toLowerCase() === 'active'
					&& (!isRestrictedNavigatorReport || isSuperUserRoleIdOne);
			})
			.map((row) => {
				const secondary = Array.isArray(row.secondaryTags) ? row.secondaryTags : [];
				const resolvedId = row.id ?? (row as any).reportId;
				const resolvedName = row.name || (row as any).reportName || '';
				const primaryTag = (row.primaryTag || '').trim();
				const normalizedSecondaryTags = secondary
					.map((tag) => String(tag ?? '').trim())
					.filter(Boolean);
				const categories = Array.from(new Set([row.primaryTag, ...secondary].filter(Boolean))) as ReportCategory[];
				const link = (row.link && row.link.trim()) || 'TBD';
				const platform = row.reportType === 'INTERNAL_REDIRECT' ? 'Navigator' : inferPlatformFromLink(link);
				const kpis = Array.isArray(row.kpiTags) ? row.kpiTags : [];
				const icon = (row.icon && row.icon.trim()) || ((row as any).iconName && (row as any).iconName.trim()) || 'analytics';

				return {
					id: String(resolvedId),
					name: resolvedName,
					description: row.description || (row as any).reportDescription || '',
					categories,
					platform,
					url: link,
					kpis,
					icon,
					openInApp: Boolean((row as any).openInApp),
					primaryTag,
					secondaryTags: normalizedSecondaryTags,
				};
			})
			.sort((left, right) => left.name.localeCompare(right.name));
	}, [reportConfigRows, userRoleId]);
	const isIpad = Platform.OS === 'ios' && Math.min(width, height) >= 768 && Math.max(width, height) <= 1366;
	const isMobile = width < 768;
	const columns = useMemo(() => {
		if (isMobile) {
			return 1;
		}
		if (isIpad) {
			return 2;
		}
		return 3;
	}, [isIpad, isMobile]);

	const categoryCounts = useMemo(() => buildCategoryCounts(configuredCatalog), [configuredCatalog]);
	const favoriteCount = useMemo(
		() => configuredCatalog.filter((report) => favoriteIds.includes(report.id)).length,
		[configuredCatalog, favoriteIds]
	);
	const reportCards = useMemo(
		() =>
			filterReports(configuredCatalog, {
				searchTerm,
				selectedCategories,
				favoriteIds,
				favoritesOnly,
			}),
		[configuredCatalog, searchTerm, selectedCategories, favoriteIds, favoritesOnly]
	);
	const favoriteCards = useMemo(
		() =>
			filterReports(configuredCatalog, {
				searchTerm,
				favoriteIds,
				selectedCategories,
				favoritesOnly: true,
			}),
		[configuredCatalog, favoriteIds, searchTerm, selectedCategories]
	);
	const nonFavoriteCards = useMemo(
		() => reportCards.filter((card) => !favoriteIds.includes(card.id)),
		[reportCards, favoriteIds]
	);
	const hasSearchOrFilters = favoritesOnly || selectedCategories.length > 0 || !!searchTerm.trim();

	// The report config is now fetched by the parent (AnalyticsHubDashBoard) on mount.
	// We derive loading state from the parent prop + whether Redux has data.
	useEffect(() => {
		if (isReportConfigLoading) {
			setIsLoadingReports(true);
		} else {
			setIsLoadingReports(false);
		}
	}, [isReportConfigLoading]);

	// ─── Pre-applied filter from KPI Overview ─────────────────────────────────
	// When the user clicks a KPI card's Power BI badge in the KPI Overview tab, the
	// dashboard switches to this tab and passes the KPI name down via `preAppliedFilter`.
	// We apply it as the search term and clear category/favorite chips so only matching
	// reports are visible. When the filter is cleared (parent passes null/undefined),
	// we reset the search term so the catalog returns to its default state.
	useEffect(() => {
		if (preAppliedFilter && preAppliedFilter.trim()) {
			setSearchTerm(preAppliedFilter);
			setSelectedCategories([]);
			setFavoritesOnly(false);
		} else if (preAppliedFilter === null) {
			setSearchTerm('');
		}
	}, [preAppliedFilter]);

	useEffect(() => {
		let isMounted = true;

		const loadFavorites = async () => {
			// Try server first
			try {
				const serverFavorites: Array<{ id: number; userId: string; reportId: number; isFavourite: boolean }> = await reportsFavoritesApi.fetchFavorites();
				if (!isMounted) return;

				const ids = serverFavorites.map((f) => String(f.reportId));
				const map: Record<string, number> = {};
				serverFavorites.forEach((f) => {
					map[String(f.reportId)] = f.id;
				});
				setFavoriteIds(ids);
				setFavoriteIdMap(map);
				return;
			} catch (err) {
				// Server call failed — fall back to local storage below
			}

			try {
				const saved = await storageAdapter.getItem(REPORT_FAVORITES_STORAGE_KEY);
				if (!isMounted || !saved) return;

				const parsed = JSON.parse(saved);
				if (Array.isArray(parsed)) {
					setFavoriteIds(parsed.filter((v): v is string => typeof v === 'string'));
				} else if (parsed && Array.isArray(parsed.ids)) {
					setFavoriteIds(parsed.ids.map(String));
					setFavoriteIdMap(parsed.map || {});
				}
			} catch {
				// ignore
			} finally {
				if (isMounted) {
					hasLoadedFavoritesRef.current = true;
				}
			}
		};

		void loadFavorites();

		return () => {
			isMounted = false;
		};
	}, [userGpid]);

	useEffect(() => {
		if (!hasLoadedFavoritesRef.current) {
			return;
		}

		const payload = { ids: favoriteIds, map: favoriteIdMap };

		void storageAdapter.setItem(REPORT_FAVORITES_STORAGE_KEY, JSON.stringify(payload));
	}, [favoriteIds, favoriteIdMap]);

	useEffect(() => {
		const validIds = new Set(configuredCatalog.map((report) => report.id));
		setFavoriteIds((currentValue) => currentValue.filter((id) => validIds.has(id)));
		setFavoriteIdMap((currentMap) => {
			const copy: Record<string, number> = {};
			Object.keys(currentMap).forEach((reportId) => {
				if (validIds.has(reportId)) {
					copy[reportId] = currentMap[reportId];
				}
			});
			return copy;
		});
	}, [configuredCatalog]);

	/*
	 * Drag-and-drop functionality is temporarily disabled.
	 * Previous reorder state/effects were intentionally commented out.
	 */

	const renderCategoryTag = (category: ReportCategory) => {
		const colors = CATEGORY_COLORS[category] || FALLBACK_CATEGORY_COLORS;
		const darkCategoryMap = CATEGORY_COLORS_DARK ?? CATEGORY_COLORS;
		const darkFallback = FALLBACK_CATEGORY_COLORS_DARK ?? FALLBACK_CATEGORY_COLORS;
		const palette = isDark
			? (darkCategoryMap[category] || darkFallback)
			: (CATEGORY_COLORS[category] || FALLBACK_CATEGORY_COLORS) ?? FALLBACK_CATEGORY_COLORS;

		return (
			<View key={category} style={[styles.categoryTag, { backgroundColor: palette.background }]}>
				{renderHighlightedText(category, [styles.categoryTagText, { color: palette.text }])}
			</View>
		);
	};

	const sortSecondaryTagsBySparcOrder = (tags: string[]): string[] => {
		const sparcOrder = CATEGORY_FILTER_ORDER.map((category) => category.toLowerCase());
		const getTagRank = (tag: string): number => {
			const index = sparcOrder.indexOf(tag.toLowerCase());
			return index >= 0 ? index : Number.MAX_SAFE_INTEGER;
		};

		return [...tags].sort((left, right) => {
			const rankDiff = getTagRank(left) - getTagRank(right);
			if (rankDiff !== 0) return rankDiff;
			return left.localeCompare(right);
		});
	};

	const renderLegacyCategories = (categories: ReportCategory[]) => {
		if (categories.length > 0) {
			return categories.map(renderCategoryTag);
		}
		const fallback = isDark ? (FALLBACK_CATEGORY_COLORS_DARK ?? FALLBACK_CATEGORY_COLORS) : { background: '#ECEDEF', text: '#52565D' };
		return (
			<View style={[styles.categoryTag, { backgroundColor: fallback.background }]}>
				<Text style={[styles.categoryTagText, { color: fallback.text }]}>Uncategorized</Text>
			</View>
		);
	};

	const renderCategoryFooter = (card: ReportCatalogItem) => {
		const primaryTagRaw = String((card as any).primaryTag ?? '').trim();
		const secondaryTagsRaw = Array.isArray((card as any).secondaryTags) ? (card as any).secondaryTags : [];

		if (!primaryTagRaw) {
			return renderLegacyCategories(card.categories);
		}

		const secondaryTags = secondaryTagsRaw
			.map((tag: unknown) => String(tag ?? '').trim())
			.filter(Boolean)
			.filter((tag: string) => tag.toLowerCase() !== primaryTagRaw.toLowerCase());

		const sortedSecondaryTags = sortSecondaryTagsBySparcOrder(Array.from(new Set(secondaryTags)));

		const catMap = isDark ? (CATEGORY_COLORS_DARK ?? CATEGORY_COLORS) : CATEGORY_COLORS;
		const fallback = isDark ? (FALLBACK_CATEGORY_COLORS_DARK ?? FALLBACK_CATEGORY_COLORS) : FALLBACK_CATEGORY_COLORS;
		const dividerColor = isDark ? '#3A3F4A' : '#CDD5E2';

		if (sortedSecondaryTags.length === 0) {
			const primaryColors = catMap[primaryTagRaw as ReportCategory] ?? fallback;
			return (
				<View style={[styles.categoryTag, { backgroundColor: primaryColors.background }]}>
					{renderHighlightedText(primaryTagRaw, [styles.categoryTagText, { color: primaryColors.text }])}
				</View>
			);
		}

		const primaryColors = catMap[primaryTagRaw as ReportCategory] ?? fallback;
		const shouldUseTwoLineLayout = sortedSecondaryTags.length > 2;
		let firstLineSecondaryTags = shouldUseTwoLineLayout ? sortedSecondaryTags.slice(0, 2) : sortedSecondaryTags;
		let secondLineSecondaryTags = shouldUseTwoLineLayout ? sortedSecondaryTags.slice(2) : [];

		const estimatedLineWidthScore = [primaryTagRaw, ...firstLineSecondaryTags].reduce(
			(total, tag) => total + tag.length,
			0
		);
		const shouldMoveOneSecondaryToSecondLine =
			firstLineSecondaryTags.length > 1 &&
			(estimatedLineWidthScore >= 24 || firstLineSecondaryTags.some((tag) => tag.toLowerCase() === 'navigator'));

		if (shouldMoveOneSecondaryToSecondLine) {
			const movedTag = firstLineSecondaryTags[firstLineSecondaryTags.length - 1];
			firstLineSecondaryTags = firstLineSecondaryTags.slice(0, firstLineSecondaryTags.length - 1);
			secondLineSecondaryTags = [movedTag, ...secondLineSecondaryTags];
		}

		if (secondLineSecondaryTags.length === 0) {
			return (
				<View style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
					<View style={[styles.categoryTag, { backgroundColor: primaryColors.background }]}>
						{renderHighlightedText(primaryTagRaw, [styles.categoryTagText, { color: primaryColors.text }])}
					</View>
					<Text style={{ fontSize: 14, lineHeight: 16, fontFamily: 'Inter-Regular', color: dividerColor, marginHorizontal: -2 }}>|</Text>
					{firstLineSecondaryTags.map((tag) => {
						const tagColors = catMap[tag as ReportCategory] ?? fallback;
						return (
							<View key={`${primaryTagRaw}-${tag}`} style={[styles.categoryTag, { backgroundColor: tagColors.background }]}>
								{renderHighlightedText(tag, [styles.categoryTagText, { color: tagColors.text }])}
							</View>
						);
					})}
				</View>
			);
		}

		return (
			<View style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
				<View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
					<View style={[styles.categoryTag, { backgroundColor: primaryColors.background }]}>
						{renderHighlightedText(primaryTagRaw, [styles.categoryTagText, { color: primaryColors.text }])}
					</View>
					<Text style={{ fontSize: 14, lineHeight: 16, fontFamily: 'Inter-Regular', color: dividerColor, marginHorizontal: -2 }}>|</Text>
					{firstLineSecondaryTags.map((tag) => {
						const tagColors = catMap[tag as ReportCategory] ?? fallback;
						return (
							<View key={`${primaryTagRaw}-${tag}`} style={[styles.categoryTag, { backgroundColor: tagColors.background }]}>
								{renderHighlightedText(tag, [styles.categoryTagText, { color: tagColors.text }])}
							</View>
						);
					})}
				</View>
				<View style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
					{secondLineSecondaryTags.map((tag) => {
						const tagColors = catMap[tag as ReportCategory] ?? fallback;
						return (
							<View key={`${primaryTagRaw}-${tag}`} style={[styles.categoryTag, { backgroundColor: tagColors.background }]}>
								{renderHighlightedText(tag, [styles.categoryTagText, { color: tagColors.text }])}
							</View>
						);
					})}
				</View>
			</View>
		);
	};

	const renderHighlightedText = (value: string, baseStyle: object | object[]) => {
		if (!searchTerm.trim()) {
			return <Text style={baseStyle}>{value}</Text>;
		}

		const escapedQuery = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
		const regex = new RegExp(`(${escapedQuery})`, 'ig');
		const parts = value.split(regex);

		return (
			<Text style={baseStyle}>
				{parts.map((part, index) => {
					const isMatch = part.toLowerCase() === searchTerm.toLowerCase();
					return isMatch ? (
						<Text key={`${value}-${index}`} style={styles.highlightText}>
							{part}
						</Text>
					) : (
						<Text key={`${value}-${index}`}>{part}</Text>
					);
				})}
			</Text>
		);
	};

	const renderCard = (card: ReportCatalogItem) => {
		const primaryCategory = card.categories[0];
		const catMap = isDark ? (CATEGORY_COLORS_DARK ?? CATEGORY_COLORS) : CATEGORY_COLORS;
		const fallback = isDark ? (FALLBACK_CATEGORY_COLORS_DARK ?? FALLBACK_CATEGORY_COLORS) : FALLBACK_CATEGORY_COLORS;
		const platformMap = isDark ? (PLATFORM_STYLES_DARK ?? PLATFORM_STYLES) : PLATFORM_STYLES;
		const categoryColors = primaryCategory && primaryCategory in catMap
			? catMap[primaryCategory]
			: fallback;
		const platformColors = platformMap[card.platform] || (isDark ? { background: '#2A2B2E', text: '#9AA1AB' } : { background: '#ECEDEF', text: '#52565D' });
		const isFavorite = favoriteIds.includes(card.id);
		const isHovered = hoveredReportId === card.id;
		const cardPointerProps: any =
			isWeb
				? {
					onMouseEnter: () => {
						setHoveredReportId(card.id);
					},
					onMouseLeave: () => {
						setHoveredReportId((currentValue) => (currentValue === card.id ? null : currentValue));
					},
				}
				: {};

		const cardBg = isDark ? '#202020' : '#FFFFFF';
		const cardBorderColor = isDark ? '#333740' : 'transparent';
		const cardTitleColor = isDark ? '#F2F2F2' : '#2F3137';
		const cardDescColor = isDark ? '#B0B0B0' : '#6D747E';
		const cardMetaBorderColor = isDark ? '#333740' : '#CDD5E2';

		return (
			<View key={card.id} style={[styles.cardColumn, { width: `${100 / columns}%` }]} {...cardPointerProps}>
				<View
					style={[
						styles.reportCard,
						{ backgroundColor: cardBg, borderColor: cardBorderColor },
						isWeb && isHovered && styles.reportCardHover,
					]}
				>
					<TouchableOpacity
						style={styles.cardTopRow}
						activeOpacity={0.8}
						accessibilityRole="button"
						onPress={() => {
							void handleOpenReport(card);
						}}
						accessibilityLabel={`Open ${card.name}`}
					>
						{/* Drag handle UI intentionally hidden while drag-and-drop is disabled. */}

						<View style={[styles.iconWrap, { backgroundColor: categoryColors.background }]}>
							<MaterialIcons name={getValidIconName(card.icon)} size={22} color={categoryColors.icon} />
						</View>

						<View style={styles.cardBody}>
							{renderHighlightedText(card.name, [styles.cardTitle, { color: cardTitleColor }])}
							{renderHighlightedText(card.description, [styles.cardDescription, { color: cardDescColor }])}
						</View>
					</TouchableOpacity>

					<TouchableOpacity
						style={styles.favoriteButton}
						onPress={() => handleToggleFavorite(card)}
						accessibilityRole="button"
						accessibilityLabel={`${isFavorite ? 'Remove' : 'Add'} ${card.name} ${isFavorite ? 'from' : 'to'} favorites`}
					>
						<MaterialIcons name={isFavorite ? 'star' : 'star-border'} size={21} color={isFavorite ? '#F0A500' : '#B6BCC5'} />
					</TouchableOpacity>

					<View style={[styles.cardMetaRow, { borderTopColor: cardMetaBorderColor }]}>
						<View style={styles.cardFooter}>
							{renderCategoryFooter(card)}
						</View>
						<View style={styles.platformWrap}>
							<TouchableOpacity
								onPress={() => {
									void handleOpenReport(card);
								}}
								accessibilityRole="button"
								accessibilityLabel={`Open ${card.name}`}
								activeOpacity={0.8}
								style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}
							>
								<View style={[styles.platformPill, { backgroundColor: platformColors.background }]}>
									{renderHighlightedText(card.platform, [styles.platformPillText, { color: platformColors.text }])}
								</View>
								<MaterialIcons name="chevron-right" size={24} color={isDark ? '#6B7280' : '#A9B0BC'} />
							</TouchableOpacity>
						</View>
					</View>
				</View>

			</View>
	);
};
	const toggleCategory = (category: ReportCategory) => {
		setSelectedCategories((currentValue) =>
			currentValue.includes(category)
				? currentValue.filter((item) => item !== category)
				: [...currentValue, category]
		);
	};

	const handleToggleFavorite = (card: ReportCatalogItem) => {
		const reportIdNum = Number(card.id);
		const wasFavorite = favoriteIds.includes(card.id);
		const prevIds = favoriteIds.slice();
		const prevMap = { ...favoriteIdMap };

		// optimistic update
		setFavoriteIds((currentValue) => (wasFavorite ? currentValue.filter((id) => id !== card.id) : [...currentValue, card.id]));

		toast.showToast({
			header: wasFavorite ? 'Removed from favorites' : 'Added to favorites',
			subtext: card.name,
			variant: 'success',
		});

		(async () => {
			try {
				if (!wasFavorite) {
					const created = await reportsFavoritesApi.addFavorite(reportIdNum);
					setFavoriteIdMap((m) => ({ ...m, [String(reportIdNum)]: created.id }));
				} else {
					// Always remove by reportId so backend receives reportId as query param.
					await reportsFavoritesApi.removeFavoriteByReport(reportIdNum);
					setFavoriteIdMap((m) => {
						const copy = { ...m };
						delete copy[String(reportIdNum)];
						return copy;
					});
				}
			} catch (err) {
				// revert optimistic update
				setFavoriteIds(prevIds);
				setFavoriteIdMap(prevMap);
				toast.showToast({
					header: wasFavorite ? 'Remove failed' : 'Add failed',
					subtext: `Could not ${wasFavorite ? 'remove' : 'add'} ${card.name} from favorites.`,
					variant: 'error',
				});
			}
		})();
	};

	const handleOpenReport = async (card: ReportCatalogItem) => {
		if (card.platform === 'TBD' || !card.url || card.url === 'TBD') {
			toast.showToast({
				header: `"${card.name}" link is TBD — report is not yet available.`,
				subtext: '',
				variant: 'info',
				position: 'bottom-right',
			});
			return;
		}

		// INTERNAL_REDIRECT reports: navigate to internal screen using the link as route
		if (card.platform === 'Navigator') {
			if (!card.url) {
				console.warn('Internal report missing navigation link', card);
				return;
			}
			onOpenReport?.(card.url);
			return;
		}

		if (card.openInApp) {
			onOpenReport?.(card.id);
			return;
		}

		setOpeningReportId(card.id);
		onOpenReport?.(card.id);

		try {
			if (Platform.OS === 'web') {
				const webWindow = globalThis as typeof globalThis & {
					open?: (url?: string, target?: string, features?: string) => Window | null;
				};
				webWindow.open?.(card.url, '_blank', 'noopener,noreferrer');
			} else {
				await Linking.openURL(card.url);
			}

			toast.showToast({
				header: `Connecting to ${card.platform}`,
				subtext: card.name,
				variant: 'success',
				duration: 1800,
			});
		} catch {
			toast.showToast({
				header: 'Unable to open report',
				subtext: `We could not open ${card.name}.`,
				variant: 'error',
			});
		} finally {
			setTimeout(() => {
				setOpeningReportId((currentValue) => (currentValue === card.id ? null : currentValue));
			}, 350);
		}
	};

	/*
	 * Drag-and-drop handlers are intentionally commented out while the feature is disabled.
	 * const handleDropOnReport = (...) => {}
	 * const handleMobileDragToggle = (...) => {}
	 */

	const openingReport = openingReportId ? configuredCatalog.find((report) => report.id === openingReportId) : null;
	const isAllChipActive = !favoritesOnly && selectedCategories.length === 0;
	const resultCount = isLoadingReports ? 0 : reportCards.length;
	const totalCatalogCount = isLoadingReports ? 0 : configuredCatalog.length;

	return (
		<View style={styles.container}>
			<Text style={styles.sectionTitle}>REPORTS</Text>
			<View style={styles.searchRow}>
				<View style={[styles.searchInputWrap, isSearchFocused && styles.searchInputWrapFocused]}>
					<MaterialIcons name="search" size={18} color="#7C8086" />
					<TextInput
						value={searchTerm}
						onChangeText={setSearchTerm}
						onFocus={() => setIsSearchFocused(true)}
						onBlur={() => setIsSearchFocused(false)}
						placeholder="Search reports by name, description, category, or KPI…"
						placeholderTextColor="#9AA1AB"
						style={[
							styles.searchInput,
							Platform.OS === 'web' ? ({ outlineStyle: 'none', boxShadow: 'none' } as any) : null,
						]}
					/>
					{searchTerm ? (
						<TouchableOpacity
							onPress={() => setSearchTerm('')}
							accessibilityRole="button"
							accessibilityLabel="Clear report search"
							style={styles.clearButton}
						>
							<MaterialIcons name="close" size={16} color="#6D737C" />
						</TouchableOpacity>
					) : null}
				</View>
				<View style={styles.searchMetaWrap}>
					<Text style={styles.resultCount}>{resultCount} reports</Text>
					<Text style={styles.sortStatusLabel}>Sorted A - Z</Text>
				</View>
			</View>

			<View style={styles.chipsRow}>
				<TouchableOpacity
					style={[
						styles.filterChip,
						isDark && !isAllChipActive && { backgroundColor: '#202020', borderColor: '#333740' },
						isAllChipActive && styles.filterChipActive,
					]}
					onPress={() => {
						setFavoritesOnly(false);
						setSelectedCategories([]);
					}}
					accessibilityRole="button"
					accessibilityLabel="Filter chip All"
				>
					<Text style={[styles.filterChipText, isDark && !isAllChipActive && { color: '#C8CDD5' }, isAllChipActive && styles.filterChipTextActive]}>All</Text>
					<Text style={[styles.filterChipCount, isDark && !isAllChipActive && { backgroundColor: '#2A2B2E', color: '#C8CDD5' }, isAllChipActive && styles.filterChipCountActive]}>
						{totalCatalogCount}
					</Text>
				</TouchableOpacity>
	
				<TouchableOpacity
					style={[
						styles.filterChip,
						isDark && !favoritesOnly && { backgroundColor: '#202020', borderColor: '#333740' },
						favoritesOnly && styles.filterChipActive,
					]}
					onPress={() => setFavoritesOnly((currentValue) => !currentValue)}
					accessibilityRole="button"
					accessibilityLabel="Filter chip Favorites"
				>
					<MaterialIcons name="star" size={12} color={favoritesOnly ? '#FFFFFF' : '#C89200'} />
					<Text style={[styles.filterChipText, isDark && !favoritesOnly && { color: '#C8CDD5' }, favoritesOnly && styles.filterChipTextActive]}>Favorites</Text>
					<Text style={[styles.filterChipCount, isDark && !favoritesOnly && { backgroundColor: '#2A2B2E', color: '#C8CDD5' }, favoritesOnly && styles.filterChipCountActive]}>{favoriteCount}</Text>
				</TouchableOpacity>
	
				{CATEGORY_FILTER_ORDER.map((category) => {
					const isActive = selectedCategories.includes(category);
					const darkCategoryMap = CATEGORY_COLORS_DARK ?? CATEGORY_COLORS;
					const chipColors = isDark
						? (darkCategoryMap[category] || FALLBACK_CATEGORY_COLORS_DARK || FALLBACK_CATEGORY_COLORS)
						: (CATEGORY_COLORS[category] || FALLBACK_CATEGORY_COLORS);
					return (
						<TouchableOpacity
							key={category}
							style={[
								styles.filterChip,
								isDark && !isActive && { backgroundColor: '#202020', borderColor: '#333740' },
								isActive && { backgroundColor: chipColors.background, borderColor: chipColors.background },
							]}
							onPress={() => toggleCategory(category)}
							accessibilityRole="button"
							accessibilityLabel={`Filter chip ${category}`}
						>
							<View style={[styles.filterChipDot, { backgroundColor: chipColors.icon }]} />
							<Text style={[styles.filterChipText, isDark && !isActive && { color: '#C8CDD5' }, isActive && { color: chipColors.text }]}>{category}</Text>
							<Text style={[styles.filterChipCount, isDark && !isActive && { backgroundColor: '#2A2B2E', color: '#C8CDD5' }, isActive && { backgroundColor: 'rgba(0,0,0,0.18)', color: chipColors.text }]}>
								{categoryCounts[category]}
							</Text>
						</TouchableOpacity>
					);
				})}
			</View>

			{/* Mobile reorder banner intentionally hidden while drag-and-drop is disabled. */}

			{favoriteCards.length > 0 && !favoritesOnly ? (
				<View style={styles.favoritesSection}>
					<View style={styles.favoritesHeaderRow}>
						<View style={styles.favoritesTitleRow}>
							<MaterialIcons name="star" size={16} color="#C89200" />
							<Text style={styles.favoritesTitle}>FAVORITES</Text>
						</View>
						<Text style={styles.favoritesCount}>{favoriteCards.length}</Text>
					</View>
					<View style={styles.cardsGrid}>{favoriteCards.map(renderCard)}</View>
				</View>
			) : null}

			{isLoadingReports ? (
				<View style={styles.emptyState}>
					<View style={styles.emptyStateIconWrap}>
						<ActivityIndicator size="small" color="#0B5FC6" />
					</View>
					<Text style={styles.emptyStateTitle}>Loading reports...</Text>
					<Text style={styles.emptyStateText}>Fetching the latest report configuration from backend.</Text>
				</View>
			) : reportCards.length > 0 ? (
				favoritesOnly ? (
					<View style={styles.cardsGrid}>{favoriteCards.map(renderCard)}</View>
				) : (
					<>
						{favoriteCards.length > 0 && nonFavoriteCards.length > 0 ? <View style={styles.catalogDivider} /> : null}
						<View style={styles.cardsGrid}>{nonFavoriteCards.map(renderCard)}</View>
					</>
				)
			) : (
				<View style={styles.emptyState}>
					<View style={styles.emptyStateIconWrap}>
						<MaterialIcons name={hasSearchOrFilters ? 'manage-search' : 'inventory-2'} size={26} color="#0B5FC6" />
					</View>
					<Text style={styles.emptyStateTitle}>
						{hasSearchOrFilters ? 'No reports match your search' : 'No reports configured yet'}
					</Text>
					<Text style={styles.emptyStateText}>
						{hasSearchOrFilters
							? 'Try a different keyword or clear your search to see all reports.'
							: 'No report records were returned from backend. Configure reports in Admin to see them here.'}
					</Text>
					{hasSearchOrFilters ? (
						<TouchableOpacity
							style={styles.emptyStateButton}
							onPress={() => {
								setSearchTerm('');
								setSelectedCategories([]);
								setFavoritesOnly(false);
							}}
							accessibilityRole="button"
							accessibilityLabel="Clear report search and filters"
						>
							<Text style={styles.emptyStateButtonText}>Clear filters</Text>
						</TouchableOpacity>
					) : null}
				</View>
			)}

			<Modal visible={Boolean(openingReport)} transparent animationType="fade">
				<View style={styles.loadingOverlay}>
					<View style={styles.loadingCard}>
						<ActivityIndicator size="large" color="#0B5FC6" />
						<Text style={styles.loadingTitle}>Opening report</Text>
						<Text style={styles.loadingName}>{openingReport?.name ?? ''}</Text>
						<Text style={styles.loadingSubtitle}>Connecting to {openingReport?.platform ?? 'report'}...</Text>
					</View>
				</View>
			</Modal>
		</View>
	);
};

export default ReportsTab;
